import React, { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import Banner from "../Banner";
import ErrorMessage from "../ErrorMessage";
import LinkButton from "../LinkButton";
import Stack from "../Stack";
import { request } from "../../utils/apiClient";

const requestSignInLink = (emailAddress) =>
  request("/api/login", {
    method: "POST",
    body: JSON.stringify({ email_address: emailAddress }),
    credentials: "same-origin",
    sentryLabel: "ResendSignInLink",
  });

const UnverifiedEmailBanner = ({ emailAddress }) => {
  const [status, setStatus] = useState("idle");
  const sentBanner = useRef(null);

  // The button the client used has gone, so focus would otherwise fall back to
  // the top of the page.
  useEffect(() => {
    if (status === "sent") sentBanner.current?.focus();
  }, [status]);

  const resend = async () => {
    if (status === "sending") return;
    setStatus("sending");
    try {
      await requestSignInLink(emailAddress);
      setStatus("sent");
    } catch {
      setStatus("failed");
    }
  };

  if (status === "sent") {
    return (
      <Banner variant="success" border="outline" role="status" tabIndex={-1} ref={sentBanner}>
        <p>We’ve sent a new link to {emailAddress}.</p>
      </Banner>
    );
  }

  return (
    <Banner variant="info" title="Check your inbox">
      <Stack gap="sm">
        <p>We sent a confirmation link to {emailAddress}.</p>
        {status === "failed" ? (
          <ErrorMessage as="p" role="alert">
            We couldn’t send a new link. Please try again.
          </ErrorMessage>
        ) : null}
        <p>
          Didn’t receive the email?{" "}
          {/* aria-disabled rather than disabled, which would drop keyboard focus to
              the page if the request fails */}
          <LinkButton onClick={resend} aria-disabled={status === "sending"}>
            Send a new link
          </LinkButton>
        </p>
      </Stack>
    </Banner>
  );
};

UnverifiedEmailBanner.propTypes = {
  emailAddress: PropTypes.string.isRequired,
};

export default UnverifiedEmailBanner;
